/**
 * NOTHING WAS READ HERE (Hamzah, 2026-10-20: "we never have to show the data there which we don't
 * read, the data should be 100% real, no data show gaps").
 *
 * A stretch of a chart's period in which no series read anything is shown as what it is: the line
 * breaks, and the stretch carries a light hatch labelled "no data". Never a line drawn across it
 * (the bank's Load chart joined 17 Oct to 19 Oct with a smooth slope over two silent days), never a
 * flat 0 (that reads as a real zero), never the last value carried on. One look everywhere it
 * applies, so a reader learns it once: the same diagonal as the Grid continuity strip's "Not read"
 * (`.lm-strip-gap`), lighter, because here it covers a plot instead of a 20 px bar.
 *
 * Pure: times in, spans and plot bands out. A Highcharts chart puts `noDataBands` in its x axis's
 * `plotBands`; an HTML or SVG picture lays `.lm-nodata` boxes over `unreadSpans`.
 */
import type Highcharts from 'highcharts';
import type { RangeBucket, RangeResponse } from '@/lib/api/types';
import type { Tokens } from './theme';

export interface Span {
  from: number;
  to: number;
}

const MIN = 60_000;

/** A range bucket's length. Null for a month, whose length varies, and for anything unknown. */
export function bucketMsOf(bucket: RangeBucket | string | undefined): number | null {
  switch (bucket) {
    case '1 minute':
      return MIN;
    case '3 minutes':
      return 3 * MIN;
    case '15 minutes':
      return 15 * MIN;
    case '1 hour':
      return 60 * MIN;
    case '6 hours':
      return 360 * MIN;
    case '1 day':
      return 1440 * MIN;
    case '7 days':
      return 7 * 1440 * MIN;
    default:
      return null;
  }
}

/**
 * The stretches of [`from`, `to`] in which none of `times` (the starts of the buckets that read
 * anything) fell: before the first, between two further apart than a bucket, and after the last.
 *
 * The end allows `graceMs` (two buckets unless told), because the bucket still filling and the one
 * before it reach a range read a little after the clock passes them: without the allowance every
 * live chart would end in a sliver of hatch that the next refresh takes away. The start allows a
 * bucket, because the first bucket is aligned to the bucket grid, not to `from`.
 */
export function unreadSpans(times: Iterable<number>, from: number, to: number, bucketMs: number, graceMs = 2 * bucketMs): Span[] {
  if (!(to > from) || !(bucketMs > 0)) return [];
  const ts = [...new Set(times)].filter((t) => Number.isFinite(t) && t + bucketMs > from && t <= to).sort((a, b) => a - b);
  if (ts.length === 0) return [{ from, to }];
  const out: Span[] = [];
  const first = ts[0] as number;
  if (first - from > bucketMs) out.push({ from, to: first });
  for (let i = 1; i < ts.length; i++) {
    const prev = ts[i - 1] as number;
    const t = ts[i] as number;
    if (t - prev > bucketMs) out.push({ from: prev + bucketMs, to: t });
  }
  const end = (ts[ts.length - 1] as number) + bucketMs;
  if (to - end > graceMs) out.push({ from: end, to });
  return out;
}

/** The starts of every bucket in which any of `ids` (all series when omitted) read a number. */
export function readTimes(range: RangeResponse | undefined, ids?: readonly number[]): number[] {
  const want = ids ? new Set(ids) : null;
  const out = new Set<number>();
  for (const s of range?.series ?? []) {
    if (want && !want.has(s.seriesId)) continue;
    for (const p of s.points) if (p.v !== null && Number.isFinite(p.v)) out.add(Date.parse(p.t));
  }
  return [...out];
}

/** `unreadSpans` of a range read, at the bucket it was read in; none for a month bucket. */
export function unreadOf(range: RangeResponse | undefined, from: number, to: number, ids?: readonly number[]): Span[] {
  if (!range) return [];
  const ms = bucketMsOf(range.bucket);
  return ms === null ? [] : unreadSpans(readTimes(range, ids), from, to, ms);
}

/** Below this share of the axis a band is too narrow to carry its words; the hatch says it alone. */
const LABEL_FROM = 0.05;

/** The hatch as a Highcharts pattern: the strip's diagonal, one pixel, six apart, in the border ink. */
export function noDataPattern(t: Pick<Tokens, 'border'>): Highcharts.ColorType {
  return {
    pattern: {
      path: { d: 'M 0 6 L 6 0 M -1 1 L 1 -1 M 5 7 L 7 5', stroke: t.border, strokeWidth: 1 },
      width: 6,
      height: 6,
    },
  } as unknown as Highcharts.ColorType;
}

/**
 * One hatched x-axis plot band per unread span, "no data" written in the band when it is wide enough
 * to hold the words. Drawn under the series, which have no points there anyway.
 */
export function noDataBands(t: Pick<Tokens, 'border' | 'mutedFg'>, spans: readonly Span[], axis: readonly [number, number]): Highcharts.XAxisPlotBandsOptions[] {
  const width = axis[1] - axis[0];
  return spans.map((s) => ({
    from: s.from,
    to: s.to,
    zIndex: 0,
    color: noDataPattern(t),
    className: 'lm-nodata-band',
    ...((s.to - s.from) / (width > 0 ? width : 1) >= LABEL_FROM
      ? { label: { text: 'no data', verticalAlign: 'top' as const, y: 14, style: { color: t.mutedFg, fontSize: '10px' } } }
      : {}),
  }));
}

/** A span as left/width percentages of [`from`, `to`], for an HTML box laid over a picture. */
export function spanBox(s: Span, from: number, to: number): { left: number; width: number } {
  const w = to - from;
  if (!(w > 0)) return { left: 0, width: 0 };
  const a = Math.max(from, s.from);
  const b = Math.min(to, s.to);
  return { left: ((a - from) / w) * 100, width: (Math.max(0, b - a) / w) * 100 };
}
